import { parseDate } from "@internationalized/date";
import { type Moment, moment } from "@msuite/utils/date";
import type { Nullable } from "@msuite/utils/types";
import { useRef } from "react";
import {
	DateInput as AriaDateInput,
	DateField,
	DateSegment,
	I18nProvider,
} from "react-aria-components";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { Button } from "../button";
import { InputWrapper } from "../form/input-wrapper";
import { Icon } from "../icons";
import { Popover } from "../popover";
import { cn } from "../utils";
import { DateInputCalendar } from "./date-input-calendar";
import { getPlaceholderString } from "./shared";

interface DateInputProps<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	label: Nullable<string>;
	isRequired?: boolean;
	minDate?: Date | Moment | string;
	maxDate?: Date | Moment | string;
	isNullable?: boolean;
	prefix?: string;
}

export function DateInput<T extends FieldValues>({
	name,
	control,
	label,
	isRequired,
	minDate,
	maxDate,
	isNullable,
	prefix,
}: DateInputProps<T>) {
	const dateFieldRef = useRef<HTMLDivElement>(null);

	return (
		<InputWrapper
			label={label}
			isRequired={isRequired || false}
			errors={control._formState.errors}
			name={name}
		>
			<I18nProvider locale="de-DE-u-ca-gregory">
				<Controller
					control={control}
					name={name}
					render={({ field: { value, onChange } }) => (
						<DateField
							ref={dateFieldRef}
							value={value ? parseDate(moment(value).db()) : null}
							onChange={(date) => onChange(date ? date.toString() : null)}
							className="date-field"
							hourCycle={24}
							minValue={minDate ? parseDate(moment(minDate).db()) : undefined}
							maxValue={maxDate ? parseDate(moment(maxDate).db()) : undefined}
							placeholderValue={parseDate(moment().db())}
							shouldForceLeadingZeros
						>
							{prefix && (
								<div
									className="h-full cursor-default h-full -ml-3 -my-2 py-2 pl-4 pr-3 rounded-l-base bg-primary-50 dark:bg-primary-800 border-r-[0.5px] border-color"
									onClick={() =>
										dateFieldRef.current?.querySelector<HTMLElement>('[role="spinbutton"]')?.focus()
									}
								>
									<p className="label whitespace-nowrap">{prefix}</p>
								</div>
							)}
							<AriaDateInput className="h-stack gap-0.5 w-full">
								{(segment) => (
									<DateSegment
										segment={segment}
										className={cn("date-input-segment")}
									>
										{(item) =>
											item.isPlaceholder ? (
												<span className="date-input-segment-placeholder">
													{getPlaceholderString(item)}
												</span>
											) : (
												item.text
											)
										}
									</DateSegment>
								)}
							</AriaDateInput>
							<Popover
								trigger="click"
								placement="bottom-end"
								render={() => (
									<DateInputCalendar
										control={control}
										name={name}
										minDate={minDate}
										maxDate={maxDate}
										isNullable={isNullable}
									/>
								)}
							>
								<div className="-my-2 -mr-2">
									<Button
										variant="ghost"
										size="xxs"
										icon={<Icon name="Calendar" />}
									/>
								</div>
							</Popover>
						</DateField>
					)}
				/>
			</I18nProvider>
		</InputWrapper>
	);
}
